import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { billingAuthHeaders } from '../utils/billingApi';

const PART_TIME_PRODUCT_TYPE = 'keyflo_tc_free_monthly';
const FULL_TIME_PRODUCT_TYPE = 'keyflo_tc_low_monthly';
const PLAN_SORT_ORDER = {
    [PART_TIME_PRODUCT_TYPE]: 0,
    [FULL_TIME_PRODUCT_TYPE]: 1,
};

const PLAN_FEATURES = {
    [PART_TIME_PRODUCT_TYPE]: [
        { text: 'Automatic Contract and Form processing and filing' },
        { text: 'Automatic TMS sync' },
        { text: 'Email assistant' },
        { text: 'Packet Templates' },
        { text: '10+ hours saved per deal', highlight: true },
    ],
    [FULL_TIME_PRODUCT_TYPE]: [
        { text: 'Automatic Contract and Form processing and filing' },
        { text: 'Automatic TMS sync' },
        { text: 'Email assistant' },
        { text: 'Packet Templates' },
        { text: 'Automatic Form Generation', highlight: true },
        { text: 'Free e-signatures', highlight: true },
        { text: '15+ hours saved per deal', highlight: true },
    ],
};

function fmt(cents) {
    return `$${(cents / 100).toFixed(2)}`;
}

function sortPlansLowToHigh(plans) {
    return [...(plans || [])].sort((a, b) => {
        const ao = PLAN_SORT_ORDER[a.product_type];
        const bo = PLAN_SORT_ORDER[b.product_type];
        if (ao != null || bo != null) {
            return (ao ?? 100) - (bo ?? 100);
        }
        return (a.monthly_fee_cents || 0) - (b.monthly_fee_cents || 0);
    });
}

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export default function SubscriptionManageModal({
    isOpen,
    onClose,
    summary,
    user,
    billingBase,
    getAccessTokenSilently,
    getIdTokenClaims,
    onSubscriptionUpdated,
}) {
    const [step, setStep] = useState('plans');
    const [selectedPlan, setSelectedPlan] = useState(summary?.product_type || null);
    const [feedback, setFeedback] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setStep('plans');
            setSelectedPlan(summary?.product_type || null);
            setFeedback('');
            setComment('');
            setError(null);
        }
    }, [isOpen, summary?.product_type]);

    if (!isOpen || !summary) return null;

    const subscription = summary.subscription || {};
    const cancelScheduled = Boolean(subscription.cancel_at_period_end);
    const hasStripeSubscription = Boolean(subscription.status);
    const isFreeTrial = summary.payment_user_type === 'free_trial';
    const isImmediateCancel = isFreeTrial
        ? !(summary.last_payment_at)
        : !hasStripeSubscription;
    const canCancelSubscription = (
        (summary.payment_user_type === 'subscriber' || isFreeTrial)
        && !cancelScheduled
    );
    const planChanged = selectedPlan && selectedPlan !== summary.product_type;
    const accessEnds = subscription.current_period_end
        || summary.next_payment_at
        || summary.first_charge_scheduled_at;
    const plans = sortPlansLowToHigh(summary.available_plans || []);
    const reasons = summary.cancellation_reasons || [];

    const handleCancelConfirm = async () => {
        if (!feedback) {
            setError('Please select a reason for canceling.');
            return;
        }
        try {
            setSubmitting(true);
            setError(null);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.post(`${billingBase}/subscription/cancel`, {
                email: user.email,
                feedback,
                comment: comment.trim() || undefined,
            }, { headers });
            onSubscriptionUpdated(res.data);
            if (res.data?.immediate) {
                window.dispatchEvent(new CustomEvent('keyflo-access-changed'));
            }
            setStep('plans');
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReactivate = async () => {
        try {
            setSubmitting(true);
            setError(null);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.post(`${billingBase}/subscription/reactivate`, {
                email: user.email,
            }, { headers });
            onSubscriptionUpdated(res.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangePlan = async () => {
        if (!selectedPlan || selectedPlan === summary.product_type) {
            return;
        }
        try {
            setSubmitting(true);
            setError(null);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.post(`${billingBase}/subscription/change-plan`, {
                email: user.email,
                product_type: selectedPlan,
            }, { headers });
            onSubscriptionUpdated(res.data);
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subscription-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="subscription-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="subscription-modal-title"
            >
                <div className="subscription-modal-header">
                    <h3 id="subscription-modal-title">Manage Subscription</h3>
                    <button type="button" className="subscription-modal-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {cancelScheduled && step === 'plans' && (
                    <div className="subscription-modal-notice">
                        Your subscription is scheduled to end on {fmtDate(accessEnds)}.
                        You will keep full access until then.
                    </div>
                )}

                {error && <p className="subscription-modal-error">{error}</p>}

                {step === 'plans' && (
                    <>
                        <p className="subscription-modal-lead">Choose your subscription plan</p>
                        <div className="subscription-plan-cards">
                            {plans.map((plan) => {
                                const selected = selectedPlan === plan.product_type;
                                const isCurrent = plan.product_type === summary.product_type;
                                const features = PLAN_FEATURES[plan.product_type] || [];
                                return (
                                    <button
                                        key={plan.product_type}
                                        type="button"
                                        className={`subscription-plan-card${selected ? ' selected' : ''}`}
                                        onClick={() => setSelectedPlan(plan.product_type)}
                                    >
                                        {selected && (
                                            <span className="subscription-plan-badge">
                                                {isCurrent ? 'Current plan' : 'New Plan'}
                                            </span>
                                        )}
                                        <h4>{plan.display_name}</h4>
                                        <p className="subscription-plan-price">
                                            {fmt(plan.monthly_fee_cents)}/mo + {fmt(plan.transaction_fee_cents)} per closed deal
                                        </p>
                                        {features.length > 0 && (
                                            <ul className="subscription-plan-features">
                                                {features.map((feature) => (
                                                    <li
                                                        key={feature.text}
                                                        className={feature.highlight ? 'subscription-plan-feature-highlight' : undefined}
                                                    >
                                                        {feature.text}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div
                            className={`subscription-modal-footer${
                                planChanged && !cancelScheduled
                                    ? ' subscription-modal-footer-split'
                                    : canCancelSubscription
                                        ? ' subscription-modal-footer-end'
                                        : ''
                            }`}
                        >
                            {planChanged && !cancelScheduled && (
                                <button
                                    type="button"
                                    className="subscription-modal-keep-btn"
                                    onClick={handleChangePlan}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Saving…' : 'Save plan'}
                                </button>
                            )}
                            {cancelScheduled ? (
                                <button
                                    type="button"
                                    className="subscription-modal-keep-btn"
                                    onClick={handleReactivate}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Processing…' : 'Keep Subscription'}
                                </button>
                            ) : canCancelSubscription ? (
                                <button
                                    type="button"
                                    className="subscription-modal-cancel-btn"
                                    onClick={() => {
                                        setError(null);
                                        setStep('confirm-cancel');
                                    }}
                                    disabled={submitting}
                                >
                                    Cancel Subscription
                                </button>
                            ) : null}
                        </div>
                    </>
                )}

                {step === 'confirm-cancel' && (
                    <div className="subscription-cancel-confirm">
                        <p className="subscription-modal-lead">
                            Are you sure you want to cancel?
                        </p>
                        <p className="subscription-cancel-copy">
                            {isImmediateCancel ? (
                                <>
                                    You are on a free trial with no charges. If you confirm,
                                    your KeyFlo subscription access ends immediately.
                                </>
                            ) : (
                                <>
                                    Your subscription will remain active until <strong>{fmtDate(accessEnds)}</strong>.
                                    After that date you will lose access to KeyFlo unless you resubscribe.
                                </>
                            )}
                        </p>

                        <label className="subscription-cancel-label" htmlFor="cancel-reason">
                            Why are you canceling?
                            <span className="subscription-required-asterisk" aria-hidden="true">*</span>
                        </label>
                        <select
                            id="cancel-reason"
                            className="subscription-cancel-select"
                            value={feedback}
                            required
                            aria-required="true"
                            onChange={(e) => setFeedback(e.target.value)}
                        >
                            <option value="">Select a reason…</option>
                            {reasons.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>

                        <label className="subscription-cancel-label" htmlFor="cancel-comment">
                            Additional comments (optional)
                        </label>
                        <textarea
                            id="cancel-comment"
                            className="subscription-cancel-textarea"
                            rows={3}
                            maxLength={500}
                            placeholder="Tell us more (optional)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />

                        <div className="subscription-modal-footer subscription-modal-footer-split">
                            <button
                                type="button"
                                className="subscription-modal-back-btn"
                                onClick={() => {
                                    setError(null);
                                    setStep('plans');
                                }}
                                disabled={submitting}
                            >
                                Go Back
                            </button>
                            <button
                                type="button"
                                className="subscription-modal-cancel-btn"
                                onClick={handleCancelConfirm}
                                disabled={!feedback || submitting}
                            >
                                {submitting ? 'Canceling…' : 'Confirm Cancellation'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
